import Head from 'next/head'

import type { FC, ReactNode } from 'react'
import type { SeoProps } from '@/types'

import { siteConfig } from '@/constants/site'

const absolute = (value: string, origin?: string): string => {
    if (/^https?:\/\//.test(value)) return value

    return origin ? `${origin}${value.startsWith('/') ? value : `/${value}`}` : value
}

export const Seo: FC<SeoProps> = ({
    title,
    description = siteConfig.description,
    ogImage,
    siteName = siteConfig.name,
    origin,
    path = '/',
    noIndex = false,
    jsonLd
}): ReactNode => {
    const pageTitle = title ? `${title} - ${siteName}` : siteName
    const url = origin ? `${origin}${path}` : undefined
    const image = ogImage ? absolute(ogImage, origin) : undefined
    const structuredData = jsonLd ? JSON.stringify(jsonLd).replace(/</g, '\\u003c') : null

    return (
        <Head>
            <title>
                {pageTitle}
            </title>

            <meta key='description' name='description' content={description} />
            <meta key='og:title' property='og:title' content={pageTitle} />
            <meta key='og:description' property='og:description' content={description} />
            <meta key='og:type' property='og:type' content='website' />
            <meta key='og:site_name' property='og:site_name' content={siteName} />
            <meta key='og:locale' property='og:locale' content='en_IN' />
            <meta key='twitter:card' name='twitter:card' content='summary_large_image' />
            <meta key='twitter:title' name='twitter:title' content={pageTitle} />
            <meta key='twitter:description' name='twitter:description' content={description} />

            {url && <link key='canonical' rel='canonical' href={url} />}
            {url && <meta key='og:url' property='og:url' content={url} />}
            {image && <meta key='og:image' property='og:image' content={image} />}
            {image && <meta key='twitter:image' name='twitter:image' content={image} />}
            {noIndex && <meta key='robots' name='robots' content='noindex, nofollow' />}

            {structuredData && (
                <script
                    key='json-ld'
                    type='application/ld+json'
                    dangerouslySetInnerHTML={{ __html: structuredData }}
                />
            )}
        </Head>
    )
}
